<template>
  <div class="dropdown flex flex-col px-1.5 pt-1.5" ref="dropdown" :style="{width: dropdownWidth}"
  :class="{'pointer-events-none': pausePointerEvents}">
    <!-- Search Bar (or replacer component if defined) -->
    <div class="relative">
      <transition name="search-replacer">
        <component v-if="selectedCategory?.searchReplacer" :is="selectedCategory.searchReplacer" key="search-replacer" />
        <div v-else class="flex items-center gap-1.5 p-1.5" key="search-input">
          <SearchIcon className="text-text-subdued flex-shrink-0" />
          <div class="flex-grow relative min-w-0" style="height: 20px;">
            <transition name="main-input">
              <input v-if="!selectedCategory" ref="searchInput" v-model="searchQuery" class="filter-search-input" :class="`caret-primary-${getTheme}-100`" 
              placeholder="Search..." autocomplete="off" type="text" spellcheck="false" @focus="navIndex = 0" key="main-input"/>
            </transition>
            <transition name="sub-input">
              <input v-if="selectedCategory" ref="searchInput" v-model="searchQuery" class="filter-search-input" :class="`caret-primary-${getTheme}-100`" 
              :placeholder="selectedCategory?.searchPlaceholder" autocomplete="off" type="text" spellcheck="false" @focus="navIndex = 0" key="sub-input"/>
            </transition>
          </div>
        </div>
      </transition>
    </div>
    <div ref="listContainer" class="filter-list-container scrollbar-hide"
    @transitionstart="handleTransition($event, true)"
    @scroll="handleOptionsScroll"
    :style="{ maxHeight: `${listMaxHeight}px` }">
      <!-- CATEGORIES LIST -->
      <transition name="parent-list">
        <div v-if="!selectedCategory" class="w-full flex flex-col gap-1 pb-1.5" style="width: 192px;">
          <!-- Saved Filters Button -->
          <button v-if="!searchQuery.length && allowSavedFilters" class="w-full flex items-center gap-1.5 p-1.5 rounded-md min-w-0 transition-colors duration-100 mt-1"
          :class="getButtonNavClass('savedFilters', `bg-primary-${getTheme}-10`, `bg-primary-${getTheme}-25`)"
          @click="openSavedFilters">
            <SavedFiltersIcon :class="`text-primary-${getTheme}-300 flex-shrink-0`" />
            <BaseText type="label" size="sm" :class="`flex-grow text-left text-primary-${getTheme}-300`">
              Saved Filters
            </BaseText>
            <ChevronIcon :className="`text-primary-${getTheme}-300 transform -rotate-90`" />
          </button>
          <!-- Empty State -->
          <BaseText v-if="!searchedCategories.length" type="body" size="sm" class="text-text-subdued px-2.5 py-1">
            No results found
          </BaseText>
          <button v-for="(category, index) in searchedCategories" :key="`category-${category.id}`" :ref="`category-${category.id}`"
          class="w-full flex items-center gap-1.5 p-1.5 rounded-md min-w-0 transition-colors duration-100"
          :class="[getButtonNavClass(`category-${category.id}`), {'mt-1': !allowSavedFilters && index === 0}]"
          @click="selectedCategory = category">
            <div class="w-5 h-5 flex items-center justify-center">
              <component :is="category.icon" class="text-text-subdued flex-shrink-0" stroke="#808899" />
            </div>
            <BaseText type="label" size="sm" :class="`flex-grow text-left text-text-muted`">
              {{ category.name }}
            </BaseText>
            <ChevronIcon :className="`text-icon-disabled transform -rotate-90`" />
          </button>
        </div>
      </transition>
      <!-- SELECTED CATEGORY OPTIONS -->
      <transition name="child-list">
        <div v-if="selectedCategory" class="relative w-full flex flex-col pb-1.5 min-w-0">
          <div v-if="!lockedCategoryId" class="back-btn sticky top-0 z-50 py-0.5">
            <button ref="backBtn" class="z-50 w-full flex items-center gap-1.5 p-1.5 rounded-md min-w-0 transition-colors duration-100 hover:bg-neutral-25"
            :class="getButtonNavClass('backBtn')"
            @click="selectedCategory = null">
              <ChevronIcon :className="`text-icon-normal transform rotate-90`" />
              <BaseText type="label" size="sm" class="text-text-muted">
                Back
              </BaseText>
            </button>
          </div>
          <!-- Empty State -->
          <BaseText v-if="!isComponentCategory && !searchedCategoryOptions.length" type="body" size="sm" class="text-text-subdued px-2.5 py-1">
            No results found
          </BaseText>
          <!-- Category Header (if defined) -->
          <component v-else-if="selectedCategory.categoryHeader" :is="selectedCategory.categoryHeader" />
          <!-- Saved Filters List (if selected category is saved filters) -->
          <SavedFiltersList 
            v-if="selectedCategory?.id === 'saved_filters'"
            ref="savedFilters"
            :savedFilters="searchedCategoryOptions"
            :containerRef="$refs.dropdown"
            :currentNavItem="currentNavItem"
            :searchQuery="searchQuery"
            @applyPreset="applyPreset"
            @editPreset="$emit('editPreset', $event)"
            @createPreset="$emit('createPreset')"
          />
          <!-- Custom Filter Component -->
          <component 
            v-else-if="selectedCategory?.component" 
            :is="selectedCategory.component" 
            :selection="localFilters?.componentFilters?.[selectedCategory.id] ?? selectedCategory.defaultSelection"
            @update="updateComponentFilterValue(selectedCategory.id, $event)"
          />
          <!-- Category Options -->
          <button v-else v-for="(option, index) in searchedCategoryOptions" :key="`option-${index}`" :ref="`option-${index}`" 
          class="relative w-full flex items-center pl-1.5 pr-1 rounded-md min-w-0 transition-colors duration-100"
          :class="[{ 'mt-0.5': index > 0 || selectedCategory.categoryHeader }, getButtonNavClass(`option-${index}`)]"
          @mouseenter="hoveredOptionRef = `option-${index}`" @mouseleave="hoveredOptionRef = null"
          @click="handleOptionSelect(option, option.status === 'excluded')">
            <!-- Icon Rendering -->
            <component 
              v-if="option.icon"
              :is="option.icon"
              :class="`flex-shrink-0 ${option.iconClass ?? 'text-text-subdued'}`"
              :stroke="option.iconStroke ?? '#808899'"
              v-bind="option.iconFill ? { fill: option.iconFill } : {}"
            />
            <img v-else-if="option.image" :src="option.image" class="flex-shrink-0 w-5 h-5" style="border-radius: 4px" />
            <!-- Option Name -->
            <BaseText type="label" size="sm" class="ml-1.5 py-1.5 flex-grow text-left text-text-muted truncate">
              {{ option.name }}
            </BaseText>
            <!-- Extra Actions Button -->
            <transition name="actions-btn">
              <button v-if="!option.status && !selectedCategory.disableExclude && (hoveredOptionRef === `option-${index}` || actionsMenuOption?.ref === `option-${index}`)" 
              class="ml-auto p-0.5 rounded-md transition-colors duration-100"
              :class="actionsMenuOption?.ref === `option-${index}` ? 'bg-neutral-50' : 'bg-neutral-25 hover:bg-neutral-50'"
              @click.stop="showOptionActions(`option-${index}`, option)">
                <KebabMenuIcon class="text-icon-muted transform rotate-90" />
              </button>
            </transition>
            <!-- Selection Status -->
            <transition-group name="option-label" mode="out-in">
              <OptionIncludedIcon v-if="option.status === 'included'" class="ml-auto text-secondary-green-300 pointer-events-none" key="included" />
              <OptionExcludedIcon v-else-if="option.status === 'excluded'" class="ml-auto text-secondary-red-300 pointer-events-none" key="excluded" />
            </transition-group>
            <!-- Option Extra Label (if defined) -->
            <transition name="fade">
              <component v-if="!option.status && option.optionExtraLabel && hoveredOptionRef !== `option-${index}`" 
              :is="option.optionExtraLabel" class="absolute right-1 top-1/2 transform -translate-y-1/2 pointer-events-none" key="extra-label" />
            </transition>
          </button>
          <!-- Category Footer (if defined) -->
          <component v-if="searchedCategoryOptions.length && selectedCategory.categoryFooter" :is="selectedCategory.categoryFooter" class="mt-1" />
        </div>
      </transition>
      <!-- Apply Button -->
      <transition name="apply">
        <div v-if="hasLocalChanges" ref="submitContainer" class="submit-container w-full"
        :class="selectedCategory?.id === 'saved_filters' ? 'absolute opacity-0 pointer-events-none' : 'sticky'">
          <button class="submit-btn flex justify-center px-2 py-1.5 rounded-md"
          @click="applyLocalFilters">
            <BaseText type="label" size="sm" class="text-white w-max">
              Apply
            </BaseText>
          </button>
        </div>
      </transition>
    </div>
    <!-- EXTRA ACTIONS MENU -->
    <transition name="extra-actions">
      <div v-if="actionsMenuOption" class="extra-actions-menu absolute" :style="actionsMenuPos"
      v-on-clickaway="() => { actionsMenuOption = null }">
        <button class="w-full flex items-center gap-2 p-1.5 rounded-md text-neutral-alpha-650 transition-colors duration-100 hover:bg-neutral-alpha-50 hover:text-white"
        @click="handleOptionSelect(actionsMenuOption.option, false), actionsMenuOption = null">
          <OptionIncludedIcon class="text-secondary-green-100" />
          <BaseText type="body" size="sm">
            Include
          </BaseText>
          <BaseText type="body" size="xs" class="ml-auto text-neutral-alpha-650 pr-0.5">
            Default
          </BaseText>
        </button>
        <button class="w-full flex items-center gap-2 p-1.5 rounded-md text-neutral-alpha-650 transition-colors duration-100 hover:bg-neutral-alpha-50 hover:text-white"
        @click="handleOptionSelect(actionsMenuOption.option, true), actionsMenuOption = null">
          <OptionExcludedIcon class="text-secondary-red-50" />
          <BaseText type="body" size="sm">
            Exclude
          </BaseText>
        </button>
      </div>
    </transition>
    <portal-target name="preset-actions-menu" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import smoothReflow from 'vue-smooth-reflow'
import { mixin as clickaway } from 'vue-clickaway2'

import SavedFiltersList from './dropdown/SavedFiltersList.vue'

// Icons
import SearchIcon from '../globals/Icons/SearchIcon.vue'
import ChevronIcon from '../globals/Icons/ChevronIcon.vue'
import SavedFiltersIcon from '../globals/Icons/FilterIcons/SavedFiltersIcon.vue'
import OptionIncludedIcon from '../globals/Icons/FilterIcons/OptionIncludedIcon.vue'
import OptionExcludedIcon from '../globals/Icons/FilterIcons/OptionExcludedIcon.vue'
import KebabMenuIcon from '../globals/Icons/KebabMenuIcon.vue'

const FALLBACK_LIST_MAX_HEIGHT = 400

export default {
  name: 'FilterSelectDropdown',
  mixins: [smoothReflow, clickaway],
  components: {
    SavedFiltersList,
    SearchIcon,
    ChevronIcon,
    SavedFiltersIcon,
    OptionIncludedIcon,
    OptionExcludedIcon,
    KebabMenuIcon
  },
  props: {
    activeFilters: {
      type: Object,
      default: () => ({})
    },
    // If a category is locked, the dropdown will only show options from that category
    lockedCategoryId: {
      type: String,
      default: () => null
    },
    // If empty, all filter types are allowed
    allowedFilterTypes: {
      type: Array,
      default: () => []
    },
    // If empty, all filter types are allowed (cannot be used with allowedFilterTypes)
    excludedFilterTypes: {
      type: Array,
      default: () => []
    },
    hideSavedFilters: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      // Input
      searchQuery: '',
      selectedCategory: null,
      navIndex: 0,

      // Local Filters (including unapplied)
      localFilters: {},

      // UI States
      hoveredOptionRef: null,
      actionsMenuOption: null,
      actionsMenuPos: { top: 0, left: 0 },
      listMaxHeight: FALLBACK_LIST_MAX_HEIGHT,
      isTransitioning: false,
      transitionTimeout: null,
      disableTransitionTimeout: null,
      scrollChunkCount: 1,
      isScrolling: false,
      scrollTimeout: null
    }
  },
  created () {
    this.localFilters = _.cloneDeep(this.activeFilters)
    if (this.lockedCategoryId) {
      this.selectedCategory = this.getFilterCategories.find(category => category.id === this.lockedCategoryId)
    }
  },
  mounted () {
    this.$nextTick(() => {
      switch (this.getTheme) {
        case 'blue': this.$el.style.setProperty('--button-color', '#1F69FF'); break;
        case 'green': this.$el.style.setProperty('--button-color', '#009F6D'); break;
        case 'purple': this.$el.style.setProperty('--button-color', '#6534DF'); break;
        case 'red': this.$el.style.setProperty('--button-color', '#FF5453'); break;
        case 'black': this.$el.style.setProperty('--button-color', '#121212'); break;
      }

      this.$smoothReflow({
        el: this.$refs.listContainer,
        property: 'height',
        transition: 'height 200ms ease-in-out'
      })
      this.computeListMaxHeight()
      window.addEventListener('resize', this.computeListMaxHeight)
      window.addEventListener('keydown', this.handleKeyboardNavigation)
      window.addEventListener('mousemove', this.disengageNav)
      this.$refs.searchInput.focus()
    })
  },
  beforeDestroy () {
    this.$unsmoothReflow({ el: this.$refs.listContainer })
    window.removeEventListener('resize', this.computeListMaxHeight)
    window.removeEventListener('keydown', this.handleKeyboardNavigation)
    window.removeEventListener('mousemove', this.disengageNav)
  },
  watch: {
    searchQuery () {
      // Disable height transitions while the user is typing a search query
      this.$unsmoothReflow({ el: this.$refs.listContainer })
      clearTimeout(this.disableTransitionTimeout)
      this.disableTransitionTimeout = setTimeout(() => {
        this.$smoothReflow({
          el: this.$refs.listContainer,
          property: 'height',
          transition: 'height 200ms ease-in-out'
        })
      }, 50)
    },
    selectedCategory () {
      this.searchQuery = ''
      this.navIndex = 0
      this.actionsMenuOption = null
      this.scrollChunkCount = 1
    },
    navIndex (index) {
      if (index === 0) this.$refs.searchInput.focus()
      else this.$refs.searchInput.blur()
    },
    activeFilters (newVal) {
      this.localFilters = { ...newVal }
    },
    getFilterCategories: {
      handler (newCategories) {
        // If we update filter categories, we need to update the selected category if active
        if (!this.selectedCategory) return
        this.selectedCategory = newCategories.find(category => category.id === this.selectedCategory.id)
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters('MiscModule', ['getTheme']),
    ...mapGetters('SearchModule', ['getFilterCategories']),
    searchedCategories () {
      let allowedCategories = this.allowedFilterTypes.length 
        ? this.getFilterCategories.filter(category => this.allowedFilterTypes.includes(category.id))
        : this.excludedFilterTypes.length
          ? this.getFilterCategories.filter(category => !this.excludedFilterTypes.includes(category.id))
          : this.getFilterCategories
      allowedCategories = allowedCategories.filter(category => category.id !== 'saved_filters')
      return applySearchFilter(allowedCategories, this.searchQuery)
    },
    searchedCategoryOptions () {
      const activeCategory = this.selectedCategory
      if (!activeCategory || activeCategory.component) return []

      // Map to determine the status of each option
      let categoryOptions = []
      if (activeCategory.id === 'saved_filters') {
        categoryOptions = activeCategory?.options?.map(savedFilter => ({
          ...savedFilter,
          active: this.activeFilters?.appliedPreset?.id === savedFilter.id ?? false
        }))
      } else {
        categoryOptions = activeCategory?.options?.map(option => {
          let status = null
          const optionValue = activeCategory.valueTransform(option[activeCategory.optionValueKey])
          for (const [key, valueArray] of Object.entries(this.localFilters)) {
            const [logicalOperator, categoryId] = key.split('.')
            if (categoryId === activeCategory.id && valueArray.includes(optionValue)) {
              status = logicalOperator === 'exclude' ? 'excluded' : 'included'
              break
            }
          }
          return {
            ...option,
            status
          }
        })
      }
      if (activeCategory?.scrollChunkSize && !this.searchQuery.length) {
        return categoryOptions.slice(0, this.scrollChunkCount * activeCategory.scrollChunkSize)
      }
      return applySearchFilter(categoryOptions, this.searchQuery)
    },
    hasLocalChanges () {
      return !_.isEqual(this.localFilters, this.activeFilters)
    },
    isComponentCategory () {
      return this.selectedCategory?.id === 'saved_filters' || this.selectedCategory?.component
    },
    allowSavedFilters () {
      // If we use a custom filter list, we don't allow saved filters
      return !this.hideSavedFilters && !this.allowedFilterTypes.length && !this.excludedFilterTypes.length
    },
    dropdownWidth () {
      return this.selectedCategory?.id === 'saved_filters' 
        ? '288px' 
        : this.selectedCategory?.component 
          ? this.selectedCategory.componentWidth
          : '204px'
    },
    pausePointerEvents () {
      return this.isTransitioning || this.isScrolling
    },
    navigationItems () {
      if (this.selectedCategory?.component) return []
      
      return [
        { ref: 'searchInput', action: () => {} },
        // Back Button or Saved Filters Button
        (this.selectedCategory
          ? { ref: 'backBtn', action: () => { this.selectedCategory = null } } 
          : { ref: 'savedFilters', action: () => { this.openSavedFilters() } }
        ),
        // Options
        ...(this.selectedCategory
          ? this.searchedCategoryOptions.map((option, index) => ({
            ref: `option-${index}`,
            action: this.selectedCategory.id === 'saved_filters' 
              ? () => { this.applySavedFilter(option) } 
              : () => { this.handleOptionSelect(option) }
          }))
          : this.searchedCategories.map(category => ({
            ref: `category-${category.id}`,
            action: () => { this.selectedCategory = category }
          }))
        ),
        ...(this.selectedCategory?.id === 'saved_filters' ? [{ ref: 'createPresetBtn', action: () => { this.createPreset() } }] : [])
      ]
    },
    currentNavItem () {
      return this.navIndex !== null ? this.navigationItems?.[this.navIndex] ?? null : null
    },
  },
  methods: {
    // ================= DATA METHODS ==================
    handleOptionSelect (option, isExclusion = false) {
      const category = { ...this.selectedCategory }
      const typeKey = isExclusion ? `exclude.${category.id}` : `or.${category.id}`
      
      const value = category.valueTransform(option[category.optionValueKey])
      const updatedFilters = _.cloneDeep(this.localFilters)
      if (updatedFilters[typeKey]) {
        const currentCategoryOptions = updatedFilters[typeKey]
        if (currentCategoryOptions.includes(value)) {
          if (currentCategoryOptions.length === 1) delete updatedFilters[typeKey]
          else updatedFilters[typeKey] = currentCategoryOptions.filter(option => option !== value)
        } else if (category.isSingleSelect) {
          updatedFilters[typeKey] = [value]
        } else {
          updatedFilters[typeKey] = [...currentCategoryOptions, value]
        }
      } else {
        updatedFilters[typeKey] = [value]
      }
      delete updatedFilters.appliedPreset
      this.localFilters = updatedFilters
    },
    applyLocalFilters () {
      this.$emit('applyFilters', this.localFilters)
      this.$emit('close')
    },
    applyPreset (preset) {
      // If the user already has applied filters, warn them that this will overwrite them
      if (Object.keys(this.activeFilters).length) {
        const confirmed = window.confirm('Applying this preset will overwrite your current filters.')
        if (!confirmed) return
      }
      
      const { filters: presetFilters, ...presetInfo } = preset
      this.$emit('applyFilters', {
        ...presetFilters,
        appliedPreset: presetInfo
      })
      this.$emit('close')
    },
    updateComponentFilterValue (categoryId, value) {
      const updatedFilters = _.cloneDeep(this.localFilters)
      if (!value) {
        delete updatedFilters?.componentFilters?.[categoryId]
      } else {
        updatedFilters.componentFilters = {
          ...updatedFilters.componentFilters,
          [categoryId]: value
        }
      }
      this.localFilters = updatedFilters
    },

    // ================= UI METHODS ==================

    handleTransition (event) {
      if (event.target !== event.currentTarget) return
      this.isTransitioning = true
      clearTimeout(this.transitionTimeout)
      this.transitionTimeout = setTimeout(() => { this.isTransitioning = false }, 200)
    },
    openSavedFilters () {
      this.selectedCategory = this.getFilterCategories.find(category => category.id === 'saved_filters')
    },
    showOptionActions (ref, option) {
      if (this.actionsMenuOption?.ref === ref) {
        this.actionsMenuOption = null
        return
      }
      const elm = this.$refs[ref]?.[0] ?? this.$refs[ref]
      const rect = elm.getBoundingClientRect()
      const dropdownRect = this.$refs.dropdown.getBoundingClientRect()
      this.actionsMenuPos = {
        top: `${rect.top - dropdownRect.top + 16}px`,
        left: `${dropdownRect.width - 4}px`
      }
      this.actionsMenuOption = { option, ref}
    },
    computeListMaxHeight () {
      const viewportHeight = window.innerHeight
      const listContainerTop = this.$refs.listContainer.getBoundingClientRect().top
      this.listMaxHeight = viewportHeight - listContainerTop - 32 // -32px for margin
    },
    handleOptionsScroll (event) {
      const el = event.target
      
      this.isScrolling = true
      clearTimeout(this.scrollTimeout)
      this.scrollTimeout = setTimeout(() => { this.isScrolling = false }, 100)

      if (!this.selectedCategory?.scrollChunkSize || this.searchQuery.length) return

      // If we're near the bottom of the list (<150px), mount the next chunk
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 150
      const maxChunks = Math.ceil((this.selectedCategory.options?.length ?? 0) / this.selectedCategory.scrollChunkSize)

      if (nearBottom && this.scrollChunkCount < maxChunks) {
        this.scrollChunkCount += 1
      }
    },

    // ----- KEYBOARD NAVIGATION -----
    handleKeyboardNavigation (event) {
      const validKeys = ['ArrowUp', 'ArrowDown', 'Enter', 'Escape', 'Tab']
      if (!validKeys.includes(event.key)) return
      event.preventDefault()
      switch (event.key) {
        case "ArrowDown":
          this.navigateNext(); break
        case "ArrowUp":
          this.navigatePrev(); break
        case "Enter":
          this.selectNavItem(); break
        case "Escape":
          this.handleExitNav(); break
      }
    },
    navigateNext () {
      if (this.navIndex === null || this.navIndex < this.navigationItems.length - 1) {
        this.navIndex = this.navIndex === null ? 0 : this.navIndex + 1
        this.scrollToItem(this.currentNavItem.ref)
      }
    },
    navigatePrev () {
      if (this.navIndex === 0) {
        this.navIndex = null
      } else if (this.navIndex !== null) {
        this.navIndex--
        this.scrollToItem(this.currentNavItem.ref)
      }
    },
    selectNavItem () {
      if (this.currentNavItem) this.currentNavItem.action()
    },
    handleExitNav () {
      if (this.selectedCategory && !this.lockedCategoryId) this.selectedCategory = null
      else this.$emit('close')
    },
    scrollToItem (ref) {
      this.$nextTick(() => {
        let item = this.$refs[ref]?.[0] 
          ?? this.$refs[ref] 
          ?? this.$refs?.savedFilters?.$refs?.[ref]?.[0]
          ?? this.$refs?.savedFilters?.$refs?.[ref]
        if (!item) return

        if (item.$el) item = item.$el
        const itemRect = item.getBoundingClientRect()
        const container = this.$refs.listContainer
        const containerRect = container.getBoundingClientRect()

        let scrollPosition = container.scrollTop
        const offset = 16
        const overflowTop = containerRect.top - itemRect.top + (this.selectedCategory ? 40 : 0)
        const overflowBottom = itemRect.bottom - containerRect.bottom
        if (this.navIndex === 1) {
          scrollPosition = 0
        } else if (this.navIndex === this.navigationItems.length - 1) {
          scrollPosition = container.scrollHeight
        } else if (overflowTop > 0) {
          scrollPosition = container.scrollTop - overflowTop - offset
        } else if (overflowBottom > 0) {
          scrollPosition = container.scrollTop + overflowBottom + offset
        }
        container.scrollTo({ top: scrollPosition, behavior: 'smooth' })
      })
    },
    disengageNav () {
      if (this.navIndex) {
        const isInputFocused = this.$refs.searchInput.contains(document.activeElement)
        this.navIndex = isInputFocused ? 0 : null
      }
    },
    getButtonNavClass (ref, neutralClass = '', activeClass = 'bg-neutral-25') {
      return this.currentNavItem?.ref === ref || this.actionsMenuOption?.ref === ref
        ? activeClass
        : !this.navIndex
          ? `${neutralClass} hover:${activeClass}`
          : neutralClass
    }
  }
}

const applySearchFilter = (items, searchQuery) => {
  if (!searchQuery.length) return items

  let searchItems = items.filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()))

  // Sort by the closeness of the match (closer = lower index)
  searchItems.sort((a, b) => {
    const ratioA = searchQuery.length / a.name.length
    const ratioB = searchQuery.length / b.name.length
    return ratioB - ratioA
  })

  return searchItems
}
</script>

<style scoped>
.dropdown {
  transition: width 200ms ease-in-out;
  border-radius: 12px;
  background-color: white;
  box-shadow: 0 12px 16px -8px rgba(24, 35, 68, 0.04), 0 2px 4px -1.5px rgba(24, 35, 68, 0.04), 0 1px 2px -0.75px rgba(24, 35, 68, 0.04), 0 0 0 1px rgba(0, 40, 136, 0.08);
}
.filter-list-container {
  position: relative;
  overflow-x: hidden;
  overflow-y: scroll;
  display: grid;
  grid-template-rows: 1fr auto;
}

.extra-actions-menu {
  width: 176px;
  display: flex;
  flex-direction: column;
  padding: 4px;
  background-color: #100f1b;
  border-radius: 10px;
  transform: translate(0, -50%) scale(1);
}

.filter-search-input {
  /* Reset default styles */
  -webkit-appearance: none;  /* Remove default styling in WebKit browsers */
  -moz-appearance: none;     /* Remove default styling in Firefox */
  appearance: none;          /* Remove default styling in modern browsers */
  padding: 0;
  margin: 0;
  border: none;
  font-size: inherit;
  color: inherit;
  background-color: transparent;
  outline: none;

  height: 20px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  /* Body/Small */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
}
.filter-search-input:focus {
  outline: none;
  border: none;
  box-shadow: none;
}
.filter-search-input::placeholder {
  transition: color 150ms ease-in-out;
  color: #5E6678;
}
.filter-search-input:hover::placeholder {
  color: black;
}
.filter-search-input:focus::placeholder {
  color: #A4ACB9;
}

.back-btn {
  background: linear-gradient(to bottom, white 0px, white 32px, transparent 100%);
}

@property --submit-btn-opacity-1 {
syntax: '<number>';
initial-value: 0.12;
inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-color {
  syntax: '<color>';
  inherits: false;
}

.submit-btn {
  position: absolute;
  top: 0px;
  left: 1px;
  right: 1px;
  --submit-color: var(--button-color);
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px var(--submit-color);
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out, 
    background-color 100ms ease-in-out,
    width 200ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out, 
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out;
}
.submit-btn:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}

/* Apply Button/Spacer Animations */

.spacer {
  position: sticky;
  bottom: 0;
  height: 38px;
}
.submit-container {
  /* position: sticky; */
  bottom: 0px;
  height: 38px;
  z-index: 10;
  background-color: white;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  transition: opacity 200ms ease-in-out;
}
.apply-enter-active, .apply-leave-active {
  transition: opacity 150ms ease-in-out, height 150ms ease-in-out;
}
.apply-enter-from, .apply-enter, .apply-leave-to {
  opacity: 0;
  height: 0px;
}
.apply-enter-to, .apply-leave-from {
  opacity: 1;
  height: 38px;
}

/* ================= Other Vue <transition> classes ================= */
.fade-enter-active, .fade-leave-active {
  transition: opacity 100ms ease-in-out;
}
.slow-fade-enter-active, .slow-fade-leave-active {
  transition: opacity 150ms ease-in-out;
}
.fade-enter-from, .fade-enter, .fade-leave-to, .slow-fade-enter-from, .slow-fade-enter, .slow-fade-leave-to {
  opacity: 0;
}
.fade-enter-to, .fade-leave-from, .slow-fade-enter-to, .slow-fade-leave-from {
  opacity: 1;
}

.extra-actions-enter-active, .extra-actions-leave-active {
  transition: transform 100ms ease-in-out, opacity 100ms ease-in-out;
}
.extra-actions-enter-from, .extra-actions-enter, .extra-actions-leave-to {
  opacity: 0;
  transform: translate(-12px, -50%) scale(0.9);
}
.extra-actions-enter-to, .extra-actions-leave-from {
  opacity: 1;
  transform: translate(0, -50%) scale(1);
}

.search-replacer-enter-active, .search-replacer-leave-active {
  transition: opacity 150ms ease-in-out;
}
.search-replacer-leave-active {
  position: absolute;
  top: 0px;
  left: 0px;
}
.search-replacer-enter-from, .search-replacer-enter, .search-replacer-leave-to {
  opacity: 0;
}
.search-replacer-enter-to, .search-replacer-leave-from {
  opacity: 1;
}

.main-input-leave-active {
  transition: opacity 150ms ease-in-out;
  transition-delay: 15ms;
  z-index: 10;
}
.main-input-leave-to {
  opacity: 0;
}
.main-input-leave-from {
  opacity: 1;
}

.sub-input-enter-active, .sub-input-leave-active {
  transition: opacity 150ms ease-in-out;
}
.sub-input-leave-active {
  background-color: white;
  z-index: 10;
}
.sub-input-enter-from, .sub-input-enter, .sub-input-leave-to {
  opacity: 0;
}
.sub-input-enter-to, .sub-input-leave-from {
  opacity: 1;
}

.actions-btn-enter-active, .actions-btn-leave-active {
  transition: opacity 100ms ease-in-out;
}
.actions-btn-leave-active {
  position: absolute;
  right: 4px;
  top: 4px;
}
.actions-btn-enter-from, .actions-btn-enter, .actions-btn-leave-to {
  opacity: 0;
}
.actions-btn-enter-to, .actions-btn-leave-from {
  opacity: 1;
}

.option-label-enter-active, .option-label-leave-active {
  transition: opacity 100ms ease-in-out, transform 100ms ease-in-out;
  transform-origin: center;
}
.option-label-leave-active {
  position: absolute;
  right: 4px;
  top: 6px;
}
.option-label-enter-from, .option-label-enter, .option-label-leave-to {
  opacity: 0;
  transform: scale(0.85);
}
.option-label-enter-to, .option-label-leave-from {
  opacity: 1;
  transform: scale(1);
}
.parent-list-enter-active, .parent-list-leave-active, .child-list-enter-active, .child-list-leave-active {
  transition: transform 200ms ease-in-out, opacity 200ms ease-in-out;
}
.parent-list-leave-active, .child-list-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}
.parent-list-enter-from, .parent-list-enter, .child-list-enter-from, .child-list-enter {
  opacity: 0.25;
}
.parent-list-enter-from, .parent-list-enter {
  transform: translateX(-80%);
}
.child-list-enter-from, .child-list-enter {
  transform: translateX(80%);
}
.parent-list-enter-to, .parent-list-leave-from, .child-list-enter-to, .child-list-leave-from {
  transform: translateX(0);
  opacity: 1;
}
.parent-list-leave-to {
  transform: translateX(-120%);
}
.child-list-leave-to {
  transform: translateX(120%);
}
</style>